import { useState } from "react";
import { useTranslation } from "react-i18next";

/** The translator as react-i18next hands it over, so a helper can take it without widening its type. */
type Translate = ReturnType<typeof useTranslation>["t"];
import { formatDateTime } from "../../lib/format";
import {
  COMPLAINT_OUTCOMES,
  complaintsApi,
  outcomeCarriesAmount,
  type Complaint,
  type ComplaintOutcome,
} from "../../api/complaints";
import type { ApiError } from "../../api/client";
import { validateOutcomeAmount } from "../../pages/complaintsFilter";
import { useToast } from "../Toast";
import { Select } from "../Select";
import { useSectionAccess } from "../../auth/useSectionAccess";

/**
 * How an accepted complaint is settled with the buyer.
 *
 * <p>Sales asked for the value of a complaint to be recorded (P62). Two of the five outcomes carry no
 * figure at all — replacing the goods and a corrective measure with no compensation — and the three that
 * do need a currency and the sales director's approval, which the database enforces as one act with the
 * amount. The panel therefore offers the amount only once an outcome that carries one is chosen.
 *
 * <p>The figure is commercial, so a caller who may not see prices is served the outcome without it. That
 * is why the amount can be missing while the outcome is present, and the panel says so rather than
 * showing a zero.
 *
 * <p>Writing it takes TWO keys, because the endpoint takes two: the ruling key ({@code canSettle}) and
 * write on the outcome section. The quality service rules on a complaint about the finished product
 * (KK53) and holds no outcome section at all, so a form offered on the ruling key alone would hand it a
 * refusal on the click. Where the form is gone, the panel says whose entry is awaited.
 */
export function ComplaintSettlementPanel({
  complaint,
  canSettle,
  canApproveAmount,
  onChanged,
}: {
  complaint: Complaint;
  canSettle: boolean;
  canApproveAmount: boolean;
  onChanged: (updated: Complaint) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  // Section level of the record being written, next to the ruling key the caller was handed.
  const { canWrite } = useSectionAccess("complaint_outcome");
  const [outcome, setOutcome] = useState<ComplaintOutcome | "">(complaint.outcome ?? "");
  const [amount, setAmount] = useState(complaint.outcomeAmount ?? "");
  const [currency, setCurrency] = useState(complaint.outcomeCurrency ?? "");
  const [busy, setBusy] = useState(false);

  const carriesAmount = outcome !== "" && outcomeCarriesAmount(outcome);
  const editable = canSettle && canWrite && complaint.status === "RESOLVED";

  async function save() {
    if (outcome === "") return;
    const problem = validateOutcomeAmount(carriesAmount, amount, currency);
    if (problem) {
      toast.error(
        {
          amountNotAllowed: t("complaints.error.amountNotAllowed", "Ovaj ishod se ne iskazuje iznosom."),
          currencyRequired: t("complaints.error.currencyRequired", "Iznos ide uz valutu."),
          amountInvalid: t("complaints.error.amountInvalid", "Iznos nije ispravan."),
        }[problem],
      );
      return;
    }
    if (amount.trim() && !canApproveAmount) {
      toast.error(
        t("complaints.error.amountNeedsApproval", "Iznos na reklamaciji odobrava direktor prodaje."),
      );
      return;
    }
    setBusy(true);
    try {
      const updated = await complaintsApi.setOutcome(complaint.id, {
        outcome,
        amount: amount.trim() || null,
        currency: amount.trim() ? currency.trim().toUpperCase() : null,
      });
      onChanged(updated);
      toast.success(t("complaints.toast.outcomeSaved", "Ishod upisan."));
    } catch (e) {
      toast.error(errorText(t, e as ApiError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <div className="card-h">
        <h2>{t("complaints.outcome.title", "Ishod")}</h2>
        {complaint.outcome && (
          <span className="tag ok">
            {t(`complaints.outcome.${complaint.outcome}`, { defaultValue: complaint.outcome })}
          </span>
        )}
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {complaint.outcome === null && !editable && (
          <div className="empty">{t("complaints.outcome.empty", "Ishod još nije upisan.")}</div>
        )}

        {canSettle && !canWrite && complaint.status === "RESOLVED" && (
          <p className="sub" style={{ margin: 0 }}>
            {t("complaints.outcome.needsOutcomeWrite", "Ishod upisuje prodaja; vaš nalog ga ovde samo čita.")}
          </p>
        )}

        {complaint.outcome !== null && (
          <div className="muted">
            {complaint.outcomeAmount ? (
              <>
                {t("complaints.outcome.amount", "Vrednost")}: <strong>{complaint.outcomeAmount}</strong>{" "}
                {complaint.outcomeCurrency}
                {complaint.outcomeApprovedAt && (
                  <>
                    {" · "}
                    {t("complaints.outcome.approvedBy", "Odobrio")}: {complaint.outcomeApprovedByName ?? "—"} ·{" "}
                    {formatDateTime(complaint.outcomeApprovedAt)}
                  </>
                )}
              </>
            ) : outcomeCarriesAmount(complaint.outcome) ? (
              <span className="tag neutral">{t("complaints.outcome.amountHidden", "iznos nije prikazan")}</span>
            ) : (
              t("complaints.outcome.noAmount", "Ishod se ne iskazuje iznosom.")
            )}
          </div>
        )}

        {editable && (
          <>
            <div className="toolbar">
              <Select<string>
                value={outcome}
                onChange={(v) => setOutcome((v as ComplaintOutcome) || "")}
                ariaLabel={t("complaints.outcome.pick", "Izaberi ishod")}
                style={{ minWidth: 220 }}
                options={[
                  { value: "", label: t("complaints.outcome.pick", "Izaberi ishod") },
                  ...COMPLAINT_OUTCOMES.map((o) => ({
                    value: o,
                    label: t(`complaints.outcome.${o}`, { defaultValue: o }),
                  })),
                ]}
              />
              {carriesAmount && (
                <>
                  <input
                    className="input"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    inputMode="decimal"
                    style={{ maxWidth: 140 }}
                    placeholder={t("complaints.outcome.amount", "Vrednost")}
                    aria-label={t("complaints.outcome.amount", "Vrednost")}
                  />
                  <input
                    className="input"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                    maxLength={3}
                    style={{ maxWidth: 90 }}
                    placeholder={t("complaints.outcome.currency", "Valuta")}
                    aria-label={t("complaints.outcome.currency", "Valuta")}
                  />
                </>
              )}
              <button type="button" className="btn primary sm" disabled={busy || outcome === ""} onClick={() => void save()}>
                {t("complaints.outcome.save", "Upiši ishod")}
              </button>
            </div>
            {carriesAmount && !canApproveAmount && (
              <p className="sub" style={{ margin: 0 }}>
                {t(
                  "complaints.outcome.approvalHint",
                  "Iznos odobrava direktor prodaje. Bez iznosa se ishod može upisati i sada.",
                )}
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function errorText(t: Translate, err: ApiError): string {
  switch (err.code) {
    case "OUTCOME_NEEDS_ACCEPTED_COMPLAINT":
      return t("complaints.error.outcomeNeedsAccepted", "Ishod se upisuje na prihvaćenu reklamaciju.");
    case "OUTCOME_CARRIES_NO_AMOUNT":
      return t("complaints.error.amountNotAllowed", "Ovaj ishod se ne iskazuje iznosom.");
    case "OUTCOME_CURRENCY_REQUIRED":
      return t("complaints.error.currencyRequired", "Iznos ide uz valutu.");
    case "OUTCOME_AMOUNT_NEGATIVE":
      return t("complaints.error.amountInvalid", "Iznos nije ispravan.");
    case "OUTCOME_AMOUNT_NEEDS_APPROVAL":
      return t("complaints.error.amountNeedsApproval", "Iznos na reklamaciji odobrava direktor prodaje.");
    case "COMPLAINT_DECISION_NOT_YOURS":
      return t("complaints.error.decisionNotYours", "O reklamaciji ove vrste odlučuje drugi direktor.");
    default:
      return err.message;
  }
}
